import React, { FC, Fragment, useEffect, useRef, useState } from "react";
import Logo from "shared/Logo/Logo";
import Navigation from "shared/Navigation/Navigation";
import MenuBar from "shared/MenuBar/MenuBar";
import { useLocation, useNavigate } from "react-router-dom";
import RentalCarSearchForm from "components/HeroSearchForm/(car-search-form)/RentalCarSearchForm";
import MobileSearchModal from "components/MobileSearchForm/MobileSearchModal";

export interface MainNav1Props {
  className?: string;
}

const MainNav1: FC<MainNav1Props> = ({ className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState(0);
  const [showSearchInNav, setShowSearchInNav] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const measure = () => setHeaderH(navRef.current?.offsetHeight || 0);
    // Measure immediately and also after a short delay to ensure DOM is ready
    measure();
    const timer = setTimeout(measure, 100);
    window.addEventListener("resize", measure, { passive: true } as any);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    setHeaderH(navRef.current?.offsetHeight || 0);
  }, [isScrolled]);

  useEffect(() => {
    // For non-home routes, always show searchbar
    if (location.pathname !== "/") {
      setShowSearchInNav(true);
      return;
    }
    // For home route, show searchbar only after scrolling past about-section
    const target = document.getElementById("about-section");
    const check = () => {
      if (!target) {
        setShowSearchInNav(false);
        return;
      }
      const rect = target.getBoundingClientRect();
      const navHeight = navRef.current?.offsetHeight || 0;
      const reachedOrPast = rect.top - navHeight <= 0;
      setShowSearchInNav(reachedOrPast);
    };
    check();
    window.addEventListener("scroll", check, { passive: true } as any);
    window.addEventListener("resize", check, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [location.pathname]);

  return (
    <>
      <div
        ref={navRef}
        className={`nc-MainNav1 fixed top-0 left-0 right-0 z-[1000] isolate w-full ${className}`}
        style={{ zIndex: 1000 }}
      >
        <div
          className={`!bg-[#000000] border-b border-[#FFFFFF4D] px-4 xl:px-12 flex justify-between items-center transition-all duration-300`}
        >
          <div className="w-full relative flex justify-between items-center">
            <div className="flex justify-start items-center space-x-4 sm:space-x-10">
              <Logo />
            </div>

            {(location.pathname === "/checkout" ||
              location.pathname === "/listing-car-detail" ||
              location.pathname === "/booking-details" ||
              location.pathname === "/car-listing-detail" ||
              !showSearchInNav) && (
              <div className="hidden md:flex items-center ml-auto">
                <Navigation />
              </div>
            )}

            {showSearchInNav &&
              location.pathname !== "/checkout" &&
              location.pathname !== "/listing-car-detail" &&
              location.pathname !== "/booking-details" &&
              location.pathname !== "/car-listing-detail" && (
                <div className="hidden lg:flex flex-1 justify-center px-2 md:px-4">
                  <div className="w-full max-w-6xl">
                    <RentalCarSearchForm inNavbar />
                  </div>
                </div>
              )}

            {/* Mobile/Tablet: Search Your Car button when scrolled */}
            {showSearchInNav &&
              location.pathname !== "/checkout" &&
              location.pathname !== "/listing-car-detail" &&
              location.pathname !== "/car-listing-detail" && (
                <div className="flex lg:hidden flex-1 justify-center px-2">
                  <button
                    onClick={() => setIsMobileSearchOpen(true)}
                    className="flex items-center justify-between gap-2 text-neutral-400 px-6 py-3 rounded-full transition-all w-full max-w-md hover:opacity-90"
                    style={{
                      background:
                        "linear-gradient(180deg, #333333 0%, #222222 100%)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <span className="text-sm font-medium">Search</span>

                    <span className="text-sm font-medium"></span>
                  </button>
                </div>
              )}

            <div className="flex flex-shrink-0 items-center justify-end lg:flex-none text-neutral-700 dark:text-neutral-100">
              <div className="hidden xl:flex items-center space-x-0.5">
                <div className="px-2" />
              </div>
              {/* Mobile/Tablet hamburger menu */}
              <div className="lg:hidden flex items-center">
                <MenuBar />
              </div>
              {/* Desktop menu - only show when Navigation is hidden */}
              {!(
                location.pathname === "/checkout" ||
                location.pathname === "/listing-car-detail" ||
                location.pathname === "/booking-details" ||
                location.pathname === "/car-listing-detail" ||
                !showSearchInNav
              ) && (
                <div className="hidden lg:flex items-center">
                  <MenuBar />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div aria-hidden className="w-full" style={{ height: headerH }} />

      {/* Mobile Search Modal */}
      <MobileSearchModal
        isOpen={isMobileSearchOpen}
        onClose={() => setIsMobileSearchOpen(false)}
      />
    </>
  );
};

export default MainNav1;
